import { trpc as coreQueryClient } from "../services/trpc";
import type { SingleContext } from "./operatingContext";

/**
 * Extracts the plain business ID (3rd segment) from a context string.
 * Context strings have the format: `rootBizId~role~filteredBizId`
 */
export function extractPlainBusinessId(
  contextString: string,
): string | undefined {
  const parts = contextString.split("~");
  return parts.length === 3 ? parts[2] : undefined;
}

/**
 * Resolve the user's selected context against the loaded context list.
 *
 * Tries an exact `contextId` match first, then falls back to deriving the
 * root context id (`root~role~root`) when `fullString` looks like an
 * agency-level context (its filtered segment differs from the root). If both
 * lookups miss, returns the first context in `allContexts` so callers always
 * have a usable default.
 *
 * Lifted from `useOperatingContext`'s `switchedSelectedContext` memo so the
 * provider and the legacy hook share one implementation.
 */
export function findSelectedContext(
  allContexts: SingleContext[] | undefined,
  fullString: string | undefined,
): SingleContext | undefined {
  let found = allContexts?.find((x) => x.contextId === fullString);
  if (!found && fullString) {
    const parts = fullString.split("~");
    if (parts.length === 3 && parts[0] !== parts[2]) {
      const rootContextId = `${parts[0]}~${parts[1]}~${parts[0]}`;
      found = allContexts?.find((x) => x.contextId === rootContextId);
    }
  }
  return found ?? allContexts?.[0];
}

export type TrpcUtils = ReturnType<typeof coreQueryClient.useUtils>;

export const getContextForBusiness = async (
  targetBizId: string,
  utils: TrpcUtils,
): Promise<string | undefined> => {
  const rootContexts = await utils.bizContext.getSharedAncestors.ensureData(
    { businessIds: [targetBizId] },
    { staleTime: 60 * 1000 * 60 * 24 },
  );
  return rootContexts.data[0]?.id;
};
